/**
 * Generic audit-trail panel for any record-detail surface.
 *
 * Talks to the backend's `AuditTrailMixin` endpoint:
 *
 *     GET /<resourcePath>/<recordId>/audit-trail/
 *
 * which serves field-level diffs from django-simple-history. Drop into a
 * detail page with:
 *
 *     <AuditTrailPanel resourcePath="payroll/salary-slips" recordId={slip.id} />
 *
 * or use `AuditSection` to stack it with the Frappe-style ActivityTimeline,
 * or `AuditTrailDialog` to surface it from a list-page row action.
 */
import { useQuery } from "@tanstack/react-query";
import { ScrollText } from "lucide-react";

import { ActivityTimeline } from "@/components/activity/activity-timeline";
import { EmptyState } from "@/components/empty-state";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogBody,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ErrorMessage } from "@/components/ui/error-message";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { formatDateTime, formatRelative } from "@/lib/format";

export interface AuditTrailChange {
  field: string;
  from: unknown;
  to: unknown;
}

export interface AuditTrailEntry {
  history_id: string;
  occurred_at: string;
  event: "created" | "updated" | "deleted";
  changed_by: string | null;
  changed_by_id: string | null;
  fields_changed: string[];
  changes: AuditTrailChange[];
  history_change_reason: string | null;
}

export interface AuditTrailResponse {
  tracked: boolean;
  results: AuditTrailEntry[];
  has_more: boolean;
}

const EVENT_LABEL: Record<AuditTrailEntry["event"], string> = {
  created: "Record created",
  updated: "Record updated",
  deleted: "Record deleted",
};

function renderValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value);
}

interface AuditTrailPanelProps {
  /** API resource path without leading slash, e.g. "payroll/salary-slips". */
  resourcePath: string;
  recordId: string | number;
  /** Optional noun for the card description, e.g. "salary slip". */
  recordNoun?: string;
  className?: string;
}

export function AuditTrailPanel({
  resourcePath,
  recordId,
  recordNoun = "record",
  className,
}: AuditTrailPanelProps) {
  const path = resourcePath.replace(/^\/+|\/+$/g, "");
  const q = useQuery({
    queryKey: ["audit-trail", path, String(recordId)],
    queryFn: () =>
      api.get<AuditTrailResponse>(`/${path}/${recordId}/audit-trail/`),
  });
  const rows = q.data?.results ?? [];

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-base">Audit log</CardTitle>
        <CardDescription>
          Automatic snapshot every time the {recordNoun} changes. Read-only.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {q.isLoading && <Skeleton className="h-40 w-full" />}
        {q.isError && (
          <ErrorMessage error={q.error} fallback="Couldn't load audit log." />
        )}
        {q.isSuccess && q.data.tracked === false && (
          <p className="text-sm text-muted-foreground">
            Change tracking isn't enabled for this record type yet.
          </p>
        )}
        {q.isSuccess && q.data.tracked && rows.length === 0 && (
          <EmptyState
            icon={ScrollText}
            title="No changes recorded yet"
            description="Field-level edits will appear here as a timeline."
          />
        )}
        {rows.length > 0 && (
          <ol className="relative ml-3 border-l border-slate-200">
            {rows.map((ev) => (
              <AuditTrailRow key={ev.history_id} entry={ev} />
            ))}
          </ol>
        )}
        {q.data?.has_more && (
          <p className="mt-3 text-xs text-slate-400">
            Showing the most recent changes only.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function AuditTrailRow({ entry }: { entry: AuditTrailEntry }) {
  const summary =
    entry.event === "updated"
      ? entry.fields_changed.length > 0
        ? `${entry.fields_changed.length} field${entry.fields_changed.length === 1 ? "" : "s"} changed`
        : EVENT_LABEL.updated
      : EVENT_LABEL[entry.event];

  return (
    <li className="ml-6 pb-5 last:pb-0">
      <span
        className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border-2 border-white bg-blue"
        aria-hidden="true"
      />
      <p className="text-xs tabular-nums text-slate">
        {formatDateTime(entry.occurred_at)} ·{" "}
        <span className="text-slate-400">
          {formatRelative(entry.occurred_at)}
        </span>
      </p>
      <p className="mt-1 text-sm text-navy">
        {summary}
        {entry.changed_by && (
          <span className="text-slate"> · by {entry.changed_by}</span>
        )}
      </p>
      {entry.changes.length > 0 && (
        <ul className="mt-2 space-y-1">
          {entry.changes.map((c) => (
            <li
              key={c.field}
              className="flex flex-wrap items-baseline gap-x-2 text-xs"
            >
              <span className="rounded-full border border-slate-200 bg-slate-soft px-2 py-0.5 font-mono text-[11px] text-slate">
                {c.field}
              </span>
              <span className="break-all text-slate">
                {renderValue(c.from)}{" "}
                <span aria-hidden className="text-slate-400">
                  →
                </span>{" "}
                <span className="text-navy">{renderValue(c.to)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {entry.history_change_reason && (
        <p className="mt-1 text-xs text-slate">
          {entry.history_change_reason}
        </p>
      )}
    </li>
  );
}

interface AuditSectionProps extends AuditTrailPanelProps {
  /** Backend entity label for the activity feed, e.g. "payroll.SalarySlip". */
  entityType?: string;
  entityLabel?: string;
  allowComments?: boolean;
}

/**
 * Audit log + activity feed, the same stack the Employee page uses.
 * Renders just the audit log when no `entityType` is given.
 */
export function AuditSection({
  entityType,
  entityLabel,
  allowComments = true,
  ...panelProps
}: AuditSectionProps) {
  return (
    <div className="space-y-6">
      <AuditTrailPanel {...panelProps} />
      {entityType && (
        <ActivityTimeline
          entityType={entityType}
          entityId={panelProps.recordId}
          entityLabel={entityLabel}
          allowComments={allowComments}
        />
      )}
    </div>
  );
}

interface AuditTrailDialogProps extends AuditSectionProps {
  open: boolean;
  onClose: () => void;
  title?: string;
}

/**
 * Audit trail in a dialog — for list pages whose records have no detail
 * route (leave applications, expense claims, attendance, shifts, …).
 */
export function AuditTrailDialog({
  open,
  onClose,
  title = "Audit trail",
  ...sectionProps
}: AuditTrailDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
      </DialogHeader>
      <DialogBody className="max-h-[70vh] overflow-y-auto">
        {open && <AuditSection {...sectionProps} />}
      </DialogBody>
    </Dialog>
  );
}
